import { useState } from 'react'
import { CalendarX, X } from 'lucide-react'
import type { Appointment } from '@/services/appointmentsApi'
import { appointmentsApi } from '@/services/appointmentsApi'
import { useAuthStore } from '@/store/authStore'
import { useVerticalUI } from '@/lib/verticalUI'

interface CancelAppointmentModalProps {
  appointment: Appointment
  onClose: () => void
  onCancelled: () => void
}

/**
 * Modal de cancelación (fase A3): motivo auditable (`cancelReason`) y
 * notificación opcional al paciente. Auditoría de `cancelledBy`.
 */
export default function CancelAppointmentModal({
  appointment,
  onClose,
  onCancelled,
}: CancelAppointmentModalProps) {
  const { profile } = useVerticalUI()
  const currentUser = useAuthStore((s) => s.user)

  const [cancelReason, setCancelReason] = useState<string>('')
  const [notifyPatient, setNotifyPatient] = useState<boolean>(true)
  const [customMessage, setCustomMessage] = useState<string>(
    () =>
      `${appointment.patientName ?? 'Hola'}, lamentamos que no podamos coordinar tu ${profile.terminology.primaryTransactionSingular.toLowerCase()} de ${
        appointment.serviceName ?? profile.terminology.itemSingularName.toLowerCase()
      } por ahora. ¿Preferimos buscar otro horario?`
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!currentUser?.id) {
      setError('Sesión inválida')
      return
    }
    setSaving(true)
    try {
      await appointmentsApi.cancelAppointment(appointment.id, {
        cancelledBy: currentUser.id,
        cancelReason: cancelReason.trim() || undefined,
        notifyPatient,
        customMessage: notifyPatient ? customMessage.trim() || undefined : undefined,
      })
      onCancelled()
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'No se pudo cancelar la cita')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <CalendarX className="h-5 w-5 text-red-500" />
            <h2 className="text-base font-semibold text-slate-800">
              Cancelar {profile.terminology.primaryTransactionSingular.toLowerCase()}
            </h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Motivo (auditable)</span>
            <input
              type="text"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="ej. sin disponibilidad en el horario"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-700 focus:border-red-400 focus:outline-none"
            />
          </label>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={notifyPatient}
              onChange={(e) => setNotifyPatient(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-red-500"
            />
            Notificar al {profile.terminology.customerRoleLabel.toLowerCase()} por WhatsApp
          </label>

          {notifyPatient && (
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">
                Mensaje al {profile.terminology.customerRoleLabel.toLowerCase()} (editable)
              </span>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-700 focus:border-red-400 focus:outline-none"
              />
            </label>
          )}

          {error && <p className="text-sm font-medium text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cerrar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-50"
            >
              {saving ? 'Cancelando…' : `Cancelar ${profile.terminology.primaryTransactionSingular.toLowerCase()}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
